import React, { useState, useEffect, useCallback } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@mui/material";
import axios from "axios";
import moment from "moment";
import CurrencyFormat from "react-currency-format";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

import { setInvestments } from "../../slices/userSlice";

import CustomButton from "../CustomButton";
import BankNoteIcon from "../Icons/BankNoteIcon";
import RightArrIcon from "../Icons/RightArrIcon";
import { BURL, formatNum } from "../../services/base";

const TopUpInvestment = ({ open, setOpen, investment: inv }) => {
    const [inProgress, setInProgress] = useState(false);
    const [err, setErr] = useState(null);
    console.log(inv);
    const [state, setState] = useState({
        tenure: inv.rem_days,
        rem_tenure: inv.rem_days,
        principal: inv.principal,
        new_principal: inv.principal,
        new_maturity_interest: inv.maturity_interest,
        topup_date: new Date(),
        payback_amount: 0,
        is_new_topup: !inv.topup_date,
        topup_amount: inv.topup_amount || 0,
        rate: inv.rate,
        wht_status: inv.wht_status,
    });

    const market = Number(inv.money_market);
    const topup_with_interest = Number(inv.topup_with_interest);
    const primary_principal = parseFloat(inv.principal);

    const isLeapYear = (year) => {
        return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    };

    const getDifferenceInDays = (date1, date2) => {
        const startDate = new Date(date1);
        const endDate = new Date(date2);
        startDate.setHours(0, 0, 0, 0);
        return Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));
    };

    const calculateTenure = useCallback(() => {
        if (!state.topup_date || !inv.maturity_date) return;

        const activeDate = moment(state.topup_date);
        const maturityDate = moment(inv.maturity_date).endOf("day");

        setState((prev) => ({
            ...prev,
            tenure: Math.abs(maturityDate.diff(activeDate, "days")),
        }));
    }, [state.topup_date, inv.maturity_date]);

    const updateData = useCallback(() => {
        if (inv.maturity_date && state.topup_amount !== undefined) {
            calculateTenure();
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const topupDate = new Date(state.topup_date);
        topupDate.setHours(0, 0, 0, 0);

        if (topupDate > today) return;

        let newPrincipal = primary_principal;
        const activeDate = inv.topup_date || inv.active_date;
        const diffInDays = getDifferenceInDays(activeDate, topupDate);
        const activeYear = moment(activeDate).year();
        const topupYear = moment(topupDate).year();
        let t1 = 0,
            t2 = 0;

        if (activeYear !== topupYear) {
            const activeYearEnd = moment(activeDate).endOf("year");
            t1 = Math.max(0, activeYearEnd.diff(moment(activeDate), "days"));
            t2 =
                Math.max(
                    0,
                    moment(topupDate).diff(
                        moment(activeDate).add(1, "year").startOf("year"),
                        "days"
                    )
                ) + 1;

            const yearDays1 = isLeapYear(activeYear) ? 36600 : 36500;
            const yearDays2 = isLeapYear(topupYear) ? 36600 : 36500;

            const interest1 = (primary_principal * state.rate * t1) / yearDays1;
            const interest2 = (primary_principal * state.rate * t2) / yearDays2;

            const wht1 = state.wht_status == 1 ? interest1 * 0.1 : 0;
            const wht2 = state.wht_status == 1 ? interest2 * 0.1 : 0;

            const accInterest = interest1 - wht1 + (interest2 - wht2);

            newPrincipal =
                market == 0
                    ? primary_principal + accInterest + state.topup_amount
                    : primary_principal + state.topup_amount;

            if (market == 0) {
                t1 = 0;
                t2 = moment(inv.maturity_date).diff(topupDate, "days");
            }

            const matInterest1 = (newPrincipal * state.rate * t1) / yearDays1;
            const matInterest2 = (newPrincipal * state.rate * t2) / yearDays2;

            const matWht1 = state.wht_status == 1 ? matInterest1 * 0.1 : 0;
            const matWht2 = state.wht_status == 1 ? matInterest2 * 0.1 : 0;

            const newMaturityInterest =
                matInterest1 - matWht1 + (matInterest2 - matWht2);
            const paybackAmount =
                newPrincipal - primary_principal - state.topup_amount;

            setState((prev) => ({
                ...prev,
                new_principal: newPrincipal,
                new_maturity_interest: newMaturityInterest,
                payback_amount: paybackAmount,
            }));
        } else {
            const jan1NextYear = moment(topupDate)
                .add(1, "year")
                .startOf("year");
            const maturityYear = parseInt(
                moment(inv.maturity_date).format("YYYY")
            );

            if (market == 0) {
                t1 = Math.min(
                    Math.ceil(
                        moment
                            .duration(
                                moment(jan1NextYear)
                                    .subtract(1, "day")
                                    .diff(topupDate)
                            )
                            .asDays()
                    ),
                    state.tenure
                );
                t2 = Math.max(state.tenure - t1, 0);

                const yearDays = isLeapYear(activeYear) ? 36600 : 36500;
                const interest =
                    (primary_principal * state.rate * diffInDays) / yearDays;
                const wht = state.wht_status == 1 ? interest * 0.1 : 0;
                const accInterest = interest - wht;

                newPrincipal =
                    primary_principal + accInterest + state.topup_amount;

                const matInterest1 =
                    (newPrincipal * state.rate * t1) / yearDays;
                const matInterest2 =
                    (newPrincipal * state.rate * t2) / yearDays;

                const matWht1 = state.wht_status == 1 ? matInterest1 * 0.1 : 0;
                const matWht2 = state.wht_status == 1 ? matInterest2 * 0.1 : 0;

                const newMaturityInterest =
                    matInterest1 - matWht1 + (matInterest2 - matWht2);
                const paybackAmount =
                    newPrincipal - primary_principal - state.topup_amount;

                setState((prev) => ({
                    ...prev,
                    new_principal: newPrincipal,
                    new_maturity_interest: newMaturityInterest,
                    payback_amount: paybackAmount,
                }));
            } else if (market == 1) {
                t2 = 0;
                t1 = getDifferenceInDays(activeDate, topupDate);

                const yearDays = isLeapYear(activeYear) ? 36600 : 36500;
                const interest =
                    (primary_principal * state.rate * diffInDays) / yearDays;
                const wht = state.wht_status == 1 ? interest * 0.1 : 0;
                const accInterest = interest - wht;

                newPrincipal =
                    topup_with_interest == 0
                        ? primary_principal + state.topup_amount
                        : primary_principal + state.topup_amount + accInterest;

                const newMaturityInterest =
                    topup_with_interest == 0
                        ? (primary_principal * state.rate * t1) / yearDays -
                          (state.wht_status == 1
                              ? ((primary_principal * state.rate * t1) /
                                    yearDays) *
                                0.1
                              : 0) +
                          (primary_principal * state.rate * t2) / yearDays -
                          (state.wht_status == 1
                              ? ((primary_principal * state.rate * t2) /
                                    yearDays) *
                                0.1
                              : 0)
                        : 0;

                setState((prev) => ({
                    ...prev,
                    new_principal: newPrincipal,
                    new_maturity_interest: newMaturityInterest,
                }));
            }
        }
    }, [
        state.topup_date,
        state.topup_amount,
        state.rate,
        state.wht_status,
        state.tenure,
        inv.maturity_date,
        inv.topup_date,
        inv.active_date,
        market,
        topup_with_interest,
        primary_principal,
        calculateTenure,
    ]);

    useEffect(() => {
        const parsedActiveDate = moment(
            inv.topup_date || inv.active_date,
            "YYYY-MM-DD",
            true
        );

        if (parsedActiveDate.isValid()) {
            const minActiveDate = parsedActiveDate.toDate();
            setState((prev) => ({ ...prev, min_active_date: minActiveDate }));
        }

        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setState((prev) => ({ ...prev, min_date: tomorrow }));

        if (market === 0 || (market === 1 && topup_with_interest === 1)) {
            const newPrincipal =
                parseFloat(inv.principal) + parseFloat(inv.date_interest);
            setState((prev) => ({
                ...prev,
                principal: newPrincipal,
                new_principal: newPrincipal,
            }));
        }

        if (inv.active_date) {
            const parts = inv.active_date.split("-");
            const formattedDate = `${parts[2]}/${parts[1]}/${parts[0]}`;
            setState((prev) => ({ ...prev, f_active_date: formattedDate }));
        }

        updateData();
    }, [inv, market, topup_with_interest, updateData]);

    const topupAmountExceedsBalance = () => {
        const topupAmount = parseFloat(state.topup_amount) || 0;
        const ngnCashBalance = parseFloat(inv.ngn_cash_balance) || 0;
        return topupAmount > ngnCashBalance;
    };

    const rateOutOfRange = () => {
        if (market == 0) {
            const rate = parseFloat(state.rate) || 0;
            const minRate = parseFloat(inv.min_rate) || 0;
            const maxRate = parseFloat(inv.max_rate) || 0;
            return rate < minRate || rate > maxRate;
        }
        return false;
    };

    const handleTopupAmountChange = (e) => {
        if (!e.value) {
            setErr("Amount is required");
            return;
        } else if (Number(e.value) < 1) {
            setErr(`Amount can't be less than 1`);
            return;
        } else if (Number(e.value) > inv.cash_balance) {
            setErr(`Insufficient balance`);
            return;
        } else {
            setErr(null);
        }

        setState((state) => ({ ...state, topup_amount: e.floatValue }));
    };

    const successRes = () => {
        setOpen(false);
        Swal.fire({
            title: "Top Up Successful",
            text: "Thank you for Investing with us",
            icon: "success",
            // didClose: () => setOpen(false),
            confirmButtonText: "Continue",
        });
    };
    const errorRes = (res) => {
        Swal.fire({
            title: "Can't TopUp Investment",
            text: res,
            icon: "error",
            // didClose: () => setOpen(false),
            confirmButtonText: "Okay",
        });
    };

    const dispatch = useDispatch();
    const onSubmit = (e) => {
        e.preventDefault();
        if (err || !state.topup_amount) {
            return;
        }

        setInProgress(true);

        const payload = {
            id: inv.id,
            customer_id: inv.customer_id,
            new_principal: state.new_principal,
            tenure: state.tenure,
            payback_amount: state.payback_amount,
            new_maturity_interest: state.new_maturity_interest,
            topup_amount: state.topup_amount,
            topup_date: state.topup_date,
        };

        setInProgress(true);
        axios
            .post(`${BURL}top-up-investment`, payload)
            .then((res) => {
                if (!res?.data?.success) throw new Error(res);
                setInProgress(false);
                axios
                    .get(`${BURL}get-investments`)
                    .then((res) => {
                        if (!res?.data?.success) throw new Error(res);
                        dispatch(setInvestments(res?.data?.message));
                        setTimeout(() => {
                            window.location.reload();
                        }, 1000);
                    })
                    .catch((err) => {
                        setInProgress(false);
                        toast.error("Can't update investment list");
                    });
                successRes();
            })
            .catch((err) => {
                setInProgress(false);
                if (err?.response?.data) {
                    errorRes(err?.response?.data?.message);
                } else {
                    errorRes("An error occured, please try again.");
                }
            });
    };

    return (
        <Dialog
            fullWidth
            maxWidth={"sm"}
            PaperProps={{
                sx: { maxWidth: 500, borderRadius: 4 },
                className: "bg-[#F7F7F7] dark:bg-trov-dark dark:text-white",
            }}
            open={open}
            onClose={() => setOpen(false)}
        >
            <DialogTitle className=" mx-auto">
                <div className="space-y-4 pl-2 md:pl-4">
                    {/* Header */}
                    <div>
                        <h2 className="text-xl font-semibold text-white">
                            Top Up Investment – <span>{inv.ref_id}</span>
                        </h2>
                        <p className="text-sm text-gray-400 mt-1">
                            {inv.product_name}{" "}
                            {market === 1 && (
                                <small className="ml-2 text-xs text-black font-normal px-1 py-0.5 rounded bg-[#E1FFE599]">
                                    Money Market
                                </small>
                            )}
                        </p>
                    </div>

                    {/* Investment Info Grid (2x2 layout) */}
                    <div className=" grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4 text-sm text-gray-300">
                        {/* Row 1 */}
                        <div className="flex flex-col">
                            <span className="text-gray-400">Cash Balance</span>
                            <span className="text-white font-medium text-base">
                                ₦{formatNum(inv.cash_balance)}
                            </span>
                        </div>

                        <div className="flex flex-col">
                            <span className="text-gray-400">Principal</span>
                            <span className="text-white font-medium text-base">
                                ₦{formatNum(inv.principal)}
                            </span>
                        </div>

                        {(market === 0 ||
                            (market === 1 && topup_with_interest === 0)) && (
                            <>
                                <div className="flex flex-col">
                                    <span className="text-gray-400">
                                        Accrued Interest
                                    </span>
                                    <span className="text-white font-medium text-base">
                                        ₦{formatNum(inv.date_interest)}
                                    </span>
                                </div>
                            </>
                        )}
                        <div className="flex flex-col">
                            <span className="text-gray-400">Rate</span>
                            <span className="text-white font-medium text-base">
                                {formatNum(inv.rate)}%
                            </span>
                        </div>
                    </div>
                </div>
            </DialogTitle>

            <form onSubmit={onSubmit}>
                <DialogContent>
                    <DialogContentText className="">
                        <div className="w-full block dark:text-white max-w-[26rem] mx-auto">
                            <label className="ml-2 text-xs font-bold">
                                TopUp Amount
                            </label>
                            <div className="flex items-center relative flex-wrap">
                                <span className="absolute left-3">
                                    <BankNoteIcon />
                                </span>
                                <CurrencyFormat
                                    autoFocus
                                    thousandSeparator={true}
                                    prefix="₦ "
                                    onValueChange={handleTopupAmountChange}
                                    value={state.topup_amount}
                                    placeholder="Enter Amount"
                                    className={`w-full max-w-md border border-black dark:border-white rounded-full h-9 pr-4 pl-9 outline-none bg-transparent focus:border-primary transition text-sm ${
                                        err &&
                                        "border-red-500 dark:border-red-500"
                                    }`}
                                />
                            </div>
                            {err && (
                                <small className="absolute block ml-2 text-red-500">
                                    {err}
                                </small>
                            )}
                        </div>
                    </DialogContentText>

                    <div className="font-semibold break-words text-sm py-4 px-4 mt-4">
                        <div className="max-w-[26rem] mx-auto grid grid-cols-2 gap-2">
                            <div className="flex flex-col">
                                <span className="text-gray-400">
                                    New Principal
                                </span>
                                <span className="text-white font-medium text-base">
                                    ₦{formatNum(state.new_principal)}
                                </span>
                            </div>

                            {market === 0 && (
                                <div className="flex flex-col">
                                    <span className="text-gray-400">
                                        New Tenure (From Today)
                                    </span>
                                    <span className="text-white font-semibold text-base">
                                        ₦{formatNum(state.new_principal)}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center" }}>
                    <CustomButton
                        dark
                        loading={inProgress}
                        className="w-full max-w-[26rem] my-4 justify-center mx-2"
                        type="submit"
                        disabled={
                            topupAmountExceedsBalance() ||
                            (market === 0 && rateOutOfRange()) ||
                            state.topup_amount <= 0
                        }
                    >
                        <span>Continue</span>
                        {!inProgress && <RightArrIcon className="fill-white" />}
                    </CustomButton>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default TopUpInvestment;
